import apiClient from './axiosClient';
import type { DiscoveryRun } from '@/types';

/**
 * ADR-036 — the Knowledge Library: content is indexed ONCE into a per-tenant Library (a system-managed bank of
 * kind `library`), and every bank picks what it wants from it through links.
 *
 * Kept in its own module, NOT in `endpoints.ts`, on purpose (same reasoning as `api/siteMap.ts` and
 * `api/media.ts`): many test files mock `@/api/endpoints` with a handful of named exports, and a new export from
 * that module would come back `undefined` in every one of them.
 *
 * The Library ships dark behind `Knowledge.Library.Enabled`. While it is off the server answers every Library
 * route with 404 `{ error: "library_disabled" }` EXCEPT `GET /status`. The UI therefore treats anything other
 * than an explicit `enabled: true` from `/status` as "off" — an error, a missing body, or a missing field all
 * hide the Library surfaces (fail closed).
 *
 * Tenant: the `{tenantId}` route segment is the effective tenant the page already uses; the server binds it to
 * the JWT `tenantId` claim (ADR-008) and refuses a mismatch. Nothing here is an authority — the server enforces
 * `KnowledgeManage` on every route.
 */

export interface KnowledgeLibraryStatus {
  enabled: boolean;
  libraryBankId: string | null;
  autoGradeBanks: boolean;
}

export interface LibraryLinkedBank {
  bankId: string;
  name: string;
  /** The link's own on/off state for that bank (D3: a bank can turn a linked source off for itself). */
  enabled: boolean;
}

export interface LibrarySource {
  sourceId: string;
  displayName: string;
  url: string | null;
  kind: 'file' | 'url';
  status: string;
  chunkCount: number;
  ownerBankId: string;
  ownerBankName: string;
  /** True when the Library bank itself owns the source; false = a bank-owned source shown in the Library view. */
  isLibraryOwned: boolean;
  linkedBanks: LibraryLinkedBank[];
  createdAt: string;
}

export interface LibrarySourcesPage {
  items: LibrarySource[];
  total: number;
}

export interface LibrarySourceUsage {
  sourceId: string;
  displayName: string;
  // The backend can COUNT the bots linked to a bank but has no endpoint that names them (2026-10-17): `agents` is
  // empty today and `agentCount` is the number. Never read an empty `agents` as "no bots".
  ownerBank: { bankId: string; name: string; agentCount?: number };
  linkedBanks: Array<{
    bankId: string;
    name: string;
    agents: Array<{ agentConfigId: string; name: string }>;
    agentCount?: number;
    linkEnabled?: boolean;
  }>;
}

export interface DeleteLibrarySourceResult {
  deleted: boolean;
  unlinkedBanks: number;
}

export type LibraryGradedBy = 'system' | 'admin';

export interface BankLibraryItem {
  sourceId: string;
  displayName: string;
  url: string | null;
  status: string;
  chunkCount: number;
  ownerBankName: string;
  isLibraryOwned: boolean;
  /**
   * The article is stored in THIS bank (operator, 2026-11-01: every article is in the Library, so a bank's own
   * articles are listed too). It is in the bank without a link and cannot be added or removed from this tab.
   * Optional so an older server that does not send it reads as false.
   */
  ownedByThisBank?: boolean;
  /** This bank links the source. Always false for `ownedByThisBank`. */
  linked: boolean;
  /** Meaningful only when `linked`: the link's on/off state for this bank. */
  linkEnabled: boolean;
  /** 0-100 against THIS bank's purpose; null = not graded for this bank yet. */
  grade: number | null;
  gradeReason: string;
  gradedBy: LibraryGradedBy | null;
}

export interface BankLibraryList {
  items: BankLibraryItem[];
  ungradedCount: number;
  autoGrade: boolean;
}

export interface RefreshGradesResult {
  graded: number;
  remaining: number;
}

export interface ListLibrarySourcesQuery {
  q?: string;
  page?: number;
  pageSize?: number;
}

/** Limits that mirror the contract so the UI never builds a request the server must refuse. */
export const KNOWLEDGE_LIBRARY_LIMITS = {
  pageSize: 50,
  gradeMin: 0,
  gradeMax: 100,
} as const;

/**
 * `POST /grades/refresh` is synchronous and grades up to `Knowledge.Library.AutoGrade.MaxPerRun` (200) items with
 * the SLM; that can take minutes under load, so the client default (30 s) would cut it off mid-run.
 */
const REFRESH_GRADES_TIMEOUT_MS = 5 * 60 * 1000;

const enc = encodeURIComponent;

const libraryBase = (tenantId: string) => `/api/tenants/${enc(tenantId)}/knowledge-library`;

const bankLibraryBase = (tenantId: string, bankId: string) =>
  `/api/tenants/${enc(tenantId)}/knowledge-banks/${enc(bankId)}/library`;

export const knowledgeLibraryApi = {
  // ── Library (tenant-wide) ───────────────────────────────────────────────
  status: async (tenantId: string): Promise<KnowledgeLibraryStatus> => {
    const { data } = await apiClient.get<KnowledgeLibraryStatus>(`${libraryBase(tenantId)}/status`);
    return data;
  },
  listSources: async (tenantId: string, query: ListLibrarySourcesQuery = {}): Promise<LibrarySourcesPage> => {
    const params: Record<string, string | number> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? KNOWLEDGE_LIBRARY_LIMITS.pageSize,
    };
    if (query.q && query.q.trim()) params.q = query.q.trim();
    const { data } = await apiClient.get<LibrarySourcesPage>(`${libraryBase(tenantId)}/sources`, { params });
    return data;
  },
  getUsage: async (tenantId: string, sourceId: string): Promise<LibrarySourceUsage> => {
    const { data } = await apiClient.get<LibrarySourceUsage>(`${libraryBase(tenantId)}/sources/${enc(sourceId)}/usage`);
    return data;
  },
  /** D7: removes the source, its chunks, its pictures and every bank's link to it. */
  deleteSource: async (tenantId: string, sourceId: string): Promise<DeleteLibrarySourceResult> => {
    const { data } = await apiClient.delete<DeleteLibrarySourceResult>(`${libraryBase(tenantId)}/sources/${enc(sourceId)}`);
    return data;
  },

  // ── One bank's view of the Library ─────────────────────────────────────
  listForBank: async (tenantId: string, bankId: string): Promise<BankLibraryList> => {
    const { data } = await apiClient.get<BankLibraryList>(bankLibraryBase(tenantId, bankId));
    return data;
  },
  addLinks: async (tenantId: string, bankId: string, sourceIds: string[]): Promise<{ linked: number }> => {
    const { data } = await apiClient.post<{ linked: number }>(`${bankLibraryBase(tenantId, bankId)}/links`, { sourceIds });
    return data;
  },
  /** Removes only this bank's link; the article stays in the Library and in every other bank. */
  removeLink: async (tenantId: string, bankId: string, sourceId: string): Promise<void> => {
    await apiClient.delete(`${bankLibraryBase(tenantId, bankId)}/links/${enc(sourceId)}`);
  },
  setLinkEnabled: async (tenantId: string, bankId: string, sourceId: string, enabled: boolean): Promise<void> => {
    await apiClient.patch(`${bankLibraryBase(tenantId, bankId)}/links/${enc(sourceId)}`, { enabled });
  },
  refreshGrades: async (tenantId: string, bankId: string): Promise<RefreshGradesResult> => {
    const { data } = await apiClient.post<RefreshGradesResult>(
      `${bankLibraryBase(tenantId, bankId)}/grades/refresh`,
      undefined,
      { timeout: REFRESH_GRADES_TIMEOUT_MS },
    );
    return data;
  },
  /** Admin override; the automatic grader never overwrites an admin grade (D6). */
  setGrade: async (tenantId: string, bankId: string, sourceId: string, grade: number): Promise<void> => {
    await apiClient.put(`${bankLibraryBase(tenantId, bankId)}/grades/${enc(sourceId)}`, { grade });
  },
  /** D5: crawls INTO the Library (ungraded); this bank is graded first. Same run shape as a bank discovery. */
  discoverFromBank: async (tenantId: string, bankId: string, startUrl: string): Promise<DiscoveryRun> => {
    const { data } = await apiClient.post<DiscoveryRun>(`${bankLibraryBase(tenantId, bankId)}/discover`, { startUrl });
    return data;
  },
};

/** The server's `{ error: "<code>" }` body, when there is one. */
export function knowledgeLibraryErrorCode(e: unknown): string | null {
  const data = (e as { response?: { data?: unknown } } | null)?.response?.data;
  if (data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string') {
    return (data as { error: string }).error;
  }
  return null;
}

/**
 * Reads `/status` and returns a status that is ONLY enabled when the server explicitly said `enabled: true`. Any
 * error or a malformed body collapses to "off" — the caller never has to decide what an unknown means.
 *
 * `libraryBankId` may legitimately be null on an enabled Library (ADR-036 D1: the bank is created on first use);
 * the surfaces that need the bank id (Discover / Upload / Media) say so instead of guessing an id.
 */
export async function fetchKnowledgeLibraryStatusFailClosed(tenantId: string): Promise<KnowledgeLibraryStatus> {
  const off: KnowledgeLibraryStatus = { enabled: false, libraryBankId: null, autoGradeBanks: false };
  if (!tenantId) return off;
  try {
    const s = await knowledgeLibraryApi.status(tenantId);
    if (!s || s.enabled !== true) return off;
    return {
      enabled: true,
      libraryBankId: typeof s.libraryBankId === 'string' && s.libraryBankId.length > 0 ? s.libraryBankId : null,
      autoGradeBanks: s.autoGradeBanks === true,
    };
  } catch {
    return off;
  }
}
